import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button, Spin, message, Space, Typography } from 'antd';
import { MailOutlined, ReloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../AuthProvider';
import { sendEmailVerification } from 'firebase/auth';

const { Title, Text, Paragraph } = Typography;

const EmailVerificationMiddleware = ({ children }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const location = useLocation();
  
  // Check if user just signed up (coming from signup page)
  const isNewUser = location.state?.fromSignup || false;

  useEffect(() => {
    if (currentUser) {
      // Reload user to get the latest emailVerified status
      currentUser.reload().then(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const handleResendVerification = async () => {
    try {
      setSendingEmail(true);
      await sendEmailVerification(currentUser);
      message.success({
        content: 'Verification email sent successfully! Please check your inbox.',
        duration: 4,
      });
    } catch (error) {
      console.error('Error sending verification email:', error);
      message.error('Failed to send verification email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    currentUser.reload().then(() => {
      if (currentUser.emailVerified) {
        message.success('Email verified successfully!');
      } else {
        message.info('Email not yet verified. Please check your email and try again.');
      }
      setLoading(false);
    });
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          padding: '48px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #f0f0f0',
          textAlign: 'center'
        }}>
          <Spin size="large" tip="Checking verification status..." />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!currentUser.emailVerified) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          padding: '0',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #f0f0f0',
          overflow: 'hidden'
        }}>
          {/* Header Section */}
          <div style={{
            background: '#ffffff',
            padding: '32px 32px 24px 32px',
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <MailOutlined style={{ fontSize: '48px', color: '#595959' }} />
            </div>
            <Title level={2} style={{ color: '#262626', margin: 0, fontSize: '24px' }}>
              {isNewUser ? 'Welcome to VitalStaf' : 'Email Verification Required'}
            </Title>
            <Paragraph style={{ color: '#8c8c8c', fontSize: '16px', marginTop: '8px', marginBottom: 0 }}>
              {isNewUser 
                ? 'Please verify your email address to get started'
                : 'Please verify your email address to continue'
              }
            </Paragraph>
          </div>

          {/* Content Section */}
          <div style={{ padding: '32px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Email Display */}
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '16px', color: '#595959', display: 'block', marginBottom: '16px' }}>
                  We've sent a verification email to:
                </Text>
                <div style={{ 
                  padding: '16px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef',
                  marginBottom: '16px'
                }}>
                  <Text strong style={{ fontSize: '16px', color: '#262626' }}>
                    {currentUser.email}
                  </Text>
                </div>
                <Paragraph style={{ color: '#8c8c8c', margin: 0, fontSize: '14px' }}>
                  Click the verification link in your email, then click "I've Verified" below.
                </Paragraph>
              </div>

              {/* Action Buttons */}
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button 
                  type="primary" 
                  size="large"
                  block
                  icon={<CheckCircleOutlined />}
                  onClick={handleRefresh}
                  loading={loading}
                  style={{ 
                    height: '48px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    background: '#262626',
                    borderColor: '#262626'
                  }}
                >
                  I've Verified My Email
                </Button>
                
                <Button 
                  size="large"
                  block
                  icon={<ReloadOutlined />}
                  onClick={handleResendVerification}
                  loading={sendingEmail}
                  style={{ 
                    height: '48px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    borderColor: '#d9d9d9',
                    color: '#595959'
                  }}
                >
                  Resend Verification Email
                </Button>
              </Space>
            </Space>
          </div>

          {/* Help Section */}
          <div style={{ 
            background: '#f8f9fa',
            padding: '24px',
            borderTop: '1px solid #f0f0f0'
          }}>
            <Title level={5} style={{ color: '#595959', marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>
              Helpful Tips
            </Title>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text style={{ fontSize: '13px', color: '#8c8c8c', display: 'block' }}>
                • Check your spam or junk folder
              </Text>
              <Text style={{ fontSize: '13px', color: '#8c8c8c', display: 'block' }}>
                • Add our email to your contacts
              </Text>
              <Text style={{ fontSize: '13px', color: '#8c8c8c', display: 'block' }}>
                • Verification links expire in 24 hours
              </Text>
            </Space>
          </div>

          {/* Footer */}
          <div style={{ 
            padding: '16px 24px',
            textAlign: 'center',
            borderTop: '1px solid #f0f0f0',
            background: '#ffffff'
          }}>
            <Text style={{ fontSize: '13px', color: '#8c8c8c' }}>
              Need assistance? Contact our support team
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default EmailVerificationMiddleware; 